import qs from 'qs';
import { useRef } from 'react';
import { pipe } from 'fp-ts/function';
import { record } from 'fp-ts';
import { useScrollRestoration, REPORT_CONTENT_SELECTOR, UnsavedChangesObserver } from '@mews-commander/ui';
import { RouteName } from '@mews-commander/enums';
import ModalManagerEventHandler from '../../modalManager/ModalManagerEventHandler';
import { ModalTemplateFetcher } from '../../modalManager/style';
import { TemplateFetcherClass } from './TemplateFetcher';
import { usePreviousUrl } from './previousUrl';

const getScrollRestorationElement = () => document.querySelector<HTMLDivElement>(REPORT_CONTENT_SELECTOR);

interface BackendPageProps {
    url: string;
    compressQueryString?: boolean;
    loadingOverlayFixed?: boolean;
    absolutePosition?: boolean;
    withUnsavedChangesDialog?: boolean;
    /**
     * This is only needed for caching the content, see {@link CACHE_ENABLED_BACKEND_PAGES}
     */
    routeName?: RouteName;
    layout?: 'Screen' | 'Empty' | 'Modal' | 'Pdf' | 'EmptyDialog';
}

export const BackendPage = ({
    url,
    compressQueryString,
    routeName,
    loadingOverlayFixed,
    layout,
    absolutePosition = true,
    withUnsavedChangesDialog,
}: BackendPageProps) => {
    const templateFetcherRef = useRef<TemplateFetcherClass>();

    /**
     * Many of our requests to Backend controllers behave like "API actions", executing an operation on Backend and redirecting to different URL.
     * In several cases, these redirects rely on URL referer value, redirecting to previous pages, the ones where the initial request comed from.
     *
     * However, because we treat these requests as a regular URL navigation, the browser's native Referer header is providing incorrect value; it
     * sends the same value as is the actual request target is. This in turn breaks the redirects relying on this value. Because of that, we need
     * to manually send previousUrl values in a custom header.
     *
     * A proper fix is to actually identify all these actions and turn them into asynchronous background requests instead of regular anchor links.
     */
    const previousUrl = usePreviousUrl(url);

    const handlePostFormSubmit = ({
        // eslint-disable-next-line @typescript-eslint/no-shadow
        url,
        data,
        shouldHardReload,
    }: {
        url: string;
        data: FormData;
        shouldHardReload: boolean;
    }) => {
        if (templateFetcherRef.current) {
            templateFetcherRef.current.handlePostFormSubmit({ url, data, shouldHardReload });
        }
    };

    const scrollRestoration = useScrollRestoration(url, { getElement: getScrollRestorationElement });
    /**
     * TemplateFetcher needs activeWindowId to correctly navigate in case of HTTP redirect.
     * should ModalManagerEventHandler also take the latest windowId or not?
     * - the only usage is to handle some very edge case post form submit
     */
    return (
        <ModalManagerEventHandler onPostFormSubmit={handlePostFormSubmit}>
            <ModalTemplateFetcher
                absolutePosition={absolutePosition}
                layout={layout}
                loadingOverlayFixed={loadingOverlayFixed}
                ref={templateFetcherRef}
                url={url}
                previousUrl={previousUrl}
                returnHtmlOnly
                compressQueryString={compressQueryString}
                scrollRestoration={scrollRestoration}
                routeName={routeName}
            />
            {/*
             * TODO Validate if adding an alert improves our metrics
             * If it does, we will migrate this to the BE or migrate the relevant BE pages
             * Follow this issue for more information: https://mews.atlassian.net/browse/MGV-191
             */}
            {withUnsavedChangesDialog && <UnsavedChangesObserver />}
        </ModalManagerEventHandler>
    );
};

const hasOnlyDuplicateValues = (value: string | string[]): value is string[] =>
    Array.isArray(value) && value.length > 1 && new Set(value).size === 1;

export const getBackendPageUrl = ({
    pathname,
    search,
}: {
    pathname: string;
    search?: Record<string, string | string[]>;
}) => {
    if (search) {
        /**
         * In some instances TanStack router generates URLs with trailing slash.
         * Since we're adding search params, we need to ensure the slash is not there.
         */
        const noTrailingSlashPathname = pathname.replace(/\/$/, '');

        /**
         - universal-router parses arrays in query as Xyz[], tanstack router parses them as Xyz
         - in some pages, we have arrays with the same value, which shouldn't be treated as arrays
         - with this condition, the results will be in the format that backend expects
         */
        const mappedSearch = pipe(
            search,
            record.map((value) => (hasOnlyDuplicateValues(value) ? value[0] : value))
        );
        const arrayFormat = Object.keys(mappedSearch).some((key) => key.endsWith('[]')) ? 'repeat' : 'brackets';

        const stringifiedSearch = qs.stringify(mappedSearch, { arrayFormat, addQueryPrefix: true });
        return `${noTrailingSlashPathname}${stringifiedSearch}`;
    }

    return pathname;
};
